import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import RewardTable from '../../../components/tables/RewardTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { RewardClient, RewardDTO, SearchRequestDTO_rewardSearchRequestDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/config';
import axiosInstance from '../../../services/axiosInstance';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';

const RewardList: React.FC = () => {
    const [items, setItems] = useState<RewardDTO[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Rewards', href: '#' },
    ];

    const apiClient = new RewardClient(API_BASE_URL, axiosInstance);

    const fetchItems = async (page: number, size: number, filters = {}) => {
        const searchRequest: SearchRequestDTO_rewardSearchRequestDTO = {
            filters: {
                name: searchTerm || '',
                ...filters,
            },
            page,
            pageSize: size,
            sortBy: 'Name',
            sortDescending: false,
        };

        try {
            setIsLoading(true);
            const response = await apiClient.searchRewards(searchRequest);
            const totalRecords = response.data?.totalCount ?? 0;

            setItems(response.data?.results as RewardDTO[] || []);
            setTotalRecords(totalRecords);
            setTotalPages(Math.ceil(totalRecords / size));
        } catch (error) {
            console.error(`Error fetching Rewards:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchItems(1, pageSize);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        fetchItems(1, pageSize, { name: '' });
    };

    const handleDelete = async (dto: RewardDTO) => {
        const confirmed = await confirmDelete();
        if (!confirmed) return;

        try {
            await apiClient.deleteReward(dto.id || '');
            await fetchItems(currentPage, pageSize, { searchTerm });
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    return (
        <AdminLayout
            title="Rewards"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Administrator']}
        >
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-500 dark:text-gray-300">
                        Manage Rewards ({totalRecords})
                    </h2>
                    <div className="ml-auto">
                        <AddNewButton onClick={() => navigate('/admin/rewards/create')} />
                    </div>
                </div>

                <form onSubmit={handleSearch} className="mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <FormField
                            id="searchTerm"
                            label="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name"
                        />
                    </div>

                    <div className="flex space-x-4 mb-4">
                        <Button variant="secondary" onClick={handleClearSearch}>
                            Clear Search
                        </Button>
                        <Button type="submit">Search</Button>
                    </div>
                </form>

                <RewardTable
                    data={items}
                    handleEdit={(dto) =>
                        navigate(`/admin/rewards/${dto.id}`)
                    }
                    handleDelete={handleDelete}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    isLoading={isLoading}
                />
            </Card>
        </AdminLayout>
    );
};

export default RewardList;
